import {
    browserTracingIntegration,
    extraErrorDataIntegration,
    feedbackIntegration,
    httpClientIntegration,
    init,
    replayIntegration,
    sendFeedback
} from '@sentry/react';

export function initSentry(appName: string) {
    const env = process.env['NX_PUBLIC_BUILD_ENV'] ?? 'development';
    const isProduction = env === 'production';

    init({
        dsn: 'https://c9030ada0d0994b6b1232bef614c54ee@o4511348801470464.ingest.de.sentry.io/4511348809269328',
        environment: env,
        // Only send events in production — dev/edge/test errors stay local
        enabled: isProduction,
        sendDefaultPii: true,

        integrations: [
            browserTracingIntegration(),
            replayIntegration(),
            extraErrorDataIntegration(),
            httpClientIntegration(),
            // autoInject: false — no floating button; use reportBug() to submit programmatically
            feedbackIntegration({ autoInject: false })
        ],

        tracePropagationTargets: ['localhost', /https:\/\/.*\.telloport\.io/],
        // 20% of transactions traced — enough for performance insights without flooding quota
        tracesSampleRate: 0.2,
        // 100% of *traced* transactions get a profile (relative to tracesSampleRate)
        profilesSampleRate: 1.0,
        // 5% of all sessions recorded; 100% of sessions where an error occurred
        replaysSessionSampleRate: 0.05,
        replaysOnErrorSampleRate: 1.0,

        // Tag every event with which app it came from
        initialScope: {
            tags: { app: appName }
        }
    });
}

export interface BugReportParams {
    message: string;
    name?: string;
    email?: string;
    /** Sentry event ID to associate the report with (e.g. from an error boundary) */
    associatedEventId?: string;
}

/**
 * Submit a bug report to Sentry. Call this from your own form UI.
 * Returns the Sentry event ID of the submitted report, or undefined on failure.
 */
export async function reportBug(params: BugReportParams): Promise<string | undefined> {
    try {
        return await sendFeedback({
            message: params.message,
            name: params.name,
            email: params.email,
            associatedEventId: params.associatedEventId
        });
    } catch {
        return undefined;
    }
}

export { KioskErrorFallback } from './lib/kiosk-error-fallback';
export { MobileErrorFallback } from './lib/mobile-error-fallback';
export { PlatformErrorFallback } from './lib/platform-error-fallback';
export { SentryErrorBoundary } from './lib/sentry-error-boundary';
